/**
 * The homeowner disclosures for a TPO (lease or PPA), as the V1 Brochure
 * shows them. V1 and Solar Proposal V2 render this one copy, so the two never
 * disagree; change it only with product and legal sign-off.
 */

export const TPO_DISCLOSURES_HEADING = `Disclosures`

export const TPO_DISCLOSURES_INTRO = `We understand that people go solar to help the environment, and some do it to save money. Many factors impact the savings-related estimates in this Proposal and whether you will see savings. Here are some of the specific factors for you to consider in your decision:`

export const TPO_DISCLOSURES: readonly string[] = [
  `The less information you provide regarding your energy usage, rates and charges, the less accurate the estimates will be. Please provide as much information as you can, and confirm the information in this Proposal is correct.`,
  `Estimates assume your usage will remain the same after you install solar, or increase slightly. If you increase your power usage, the less power you have to sell to your utility, which may impact any potential savings presented to you in this proposal.`,
  `Solar energy production is impacted by weather, daylight hours, shade from vegetation and its future growth, local geography, and current and future human constructed structures. Changes in some of these factors are unpredictable and even small changes can impact your potential savings.`,
  `Estimates are based on your current rate plan and post-solar rate plan, and an assumption that over time your utility will increase rates in an average annual amount (all are indicated in this Proposal). However, your utility may change your future rate plans and increase your rates less than assumed.`,
  `BECAUSE OF ALL THESE VARIABLES, WE CANNOT GUARANTY THAT YOUR SYSTEM WILL RESULT IN SAVINGS. THE ENERGY USAGE AND SAVINGS ESTIMATES REFERENCED HEREIN ARE FOR DISCUSSION PURPOSES ONLY. AS A RESULT, YOU SHOULD CONSIDER EACH OF THESE FACTORS IN MAKING YOUR DECISION.`,
]

export const TPO_FAQ_HEADING = `FAQ`
export const TPO_FAQ_LEASE_AND_PPA_LABEL = `Leases & PPAs`
export const TPO_FAQ_LOAN_LABEL = `Loans`

/** One FAQ entry: a single answer, or separate answers for a lease or PPA and for a loan. */
export type TpoFaqEntry =
  | {
      question: string
      leaseAndPpa: readonly string[]
      loan: readonly string[]
    }
  | { question: string; answer: readonly string[] }

export const TPO_FAQ: readonly TpoFaqEntry[] = [
  {
    question: `Will I still receive my utility bill?`,
    answer: [
      `Yes. However, it should be significantly reduced since you’ll be generating your own electricity. Your bill will include any fixed charges plus energy you continue to purchase from your utility company.`,
    ],
  },
  {
    question: `Can I claim tax credits or incentives?`,
    leaseAndPpa: [
      `No. GoodLeap or LightReach is the owner of the solar system and claims any available tax credits, rebates, or incentives.`,
    ],
    loan: [
      `You may be eligible to claim the Residential Clean Energy Tax Credit that allows up to 30% of your total solar system price to be claimed as a personal tax credit when you purchase a solar system.`,
      `Please note, homeowners with no or low tax liability may not be able to benefit from the Federal Residential Clean Energy Tax Credit.`,
      `Again, a tax credit offsets the amount of tax you owe. If you don’t owe enough federal taxes to realize the full 30% Residential Clean Energy Tax Credit in a single year, you can roll over any unused portion of the credit on your tax return in future years.`,
      `Anyone looking to take advantage of these incentives should seek guidance from a tax professional to see if they qualify.`,
    ],
  },
  {
    question: `Are savings guaranteed?`,
    answer: [
      `No. Savings are projections based on your current use, the solar system’s expected production, and the estimated future increases in the costs for electricity charged by your utility. Your future use, shading and weather, and other unpredictable factors can all impact the savings you will see.`,
    ],
  },
  {
    question: `Who is responsible for maintenance?`,
    leaseAndPpa: [
      `GoodLeap or LightReach is responsible for monitoring or maintenance of the solar system. This makes a lease or power purchase agreement the worry-free way to go solar.`,
    ],
    loan: [
      `You are responsible for monitoring and maintenance of the solar system.`,
    ],
  },
  {
    question: `What happens if I move?`,
    leaseAndPpa: [
      `You can either prepay all the payments, purchase the system, or you may be able to transfer the agreement to the new homeowner.`,
    ],
    loan: [
      `You can either pay it off or you may be able to transfer the agreement to the new homeowner.`,
    ],
  },
  {
    question: `Is there a warranty?`,
    leaseAndPpa: [
      `With a Lease or PPA, GoodLeap & LightReach warrants your System for the Term of the Agreement and your roof for roof penetrations for 10 years.`,
    ],
    loan: [
      `Your installer will provide a roof penetration and workmanship warranties and your equipment is covered by manufacturer warranties. Check with your installer and manufacturer for details.`,
    ],
  },
]

/** The LightReach payment fine print, from already formatted figures ("$102.00", "2.99"). */
export const lightReachTpoPaymentFinePrint = (args: {
  monthlyPayment: string
  escalatorRate: string
  termYears: number
}): string =>
  `Under the terms of your solar lease or power purchase agreement (PPA), your monthly payments are designed to provide predictable energy costs over the duration of the agreement. For the first year, your monthly payments will be ${args.monthlyPayment}. Starting in the second year, your payments will increase annually by an escalator rate of ${args.escalatorRate}% for the remainder of the ${args.termYears}-year term.`

export const LIGHTREACH_TPO_FINAL_TERMS = `Final terms, including exact payment amounts, escalator rate and agreement duration, will be provided in your lease or PPA agreement.`
